/**
 * Serializable BlockNote-compatible document helpers.
 * Kept free of @blocknote imports so storage/validation stay lightweight.
 */

export const ALLOWED_NOTE_BLOCK_TYPES = [
  'paragraph',
  'heading',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'toggleListItem',
  'codeBlock',
  'divider',
  'quote',
  'table',
] as const

export type AllowedNoteBlockType = (typeof ALLOWED_NOTE_BLOCK_TYPES)[number]

const ALLOWED_TYPE_SET = new Set<string>(ALLOWED_NOTE_BLOCK_TYPES)

/** One BlockNote-style block (loose JSON shape for persistence). */
export type NoteBlock = {
  id?: string
  type: string
  props?: Record<string, unknown>
  content?: unknown
  children?: NoteBlock[]
}

export type NoteDocument = NoteBlock[]

/** Legacy plain-text note shape (schema ≤ 2). */
export type LegacyPlainNote = {
  id: string
  kataId: string
  techniqueId: string
  body: string
  createdAt: string
  updatedAt: string
}

export function emptyNoteDocument(): NoteDocument {
  return [{ type: 'paragraph', content: [] }]
}

/** Convert legacy plain text into paragraph blocks (one block per line). */
export function plainTextToNoteDocument(text: string): NoteDocument {
  const normalized = text.replace(/\r\n/g, '\n').trimEnd()
  if (!normalized.trim()) return emptyNoteDocument()
  return normalized.split('\n').map((line) => ({
    type: 'paragraph' as const,
    content: line.length > 0 ? [{ type: 'text', text: line, styles: {} }] : [],
  }))
}

function collectPlainText(value: unknown, out: string[]): void {
  if (value == null) return
  if (typeof value === 'string') {
    out.push(value)
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) collectPlainText(item, out)
    return
  }
  if (typeof value !== 'object') return
  const record = value as Record<string, unknown>
  if (typeof record.text === 'string') out.push(record.text)
  if (record.content !== undefined) collectPlainText(record.content, out)
  if (Array.isArray(record.children)) {
    for (const child of record.children) collectPlainText(child, out)
  }
  // Table cells
  if (Array.isArray(record.rows)) {
    for (const row of record.rows) collectPlainText(row, out)
  }
  if (Array.isArray(record.cells)) {
    for (const cell of record.cells) collectPlainText(cell, out)
  }
}

export function noteDocumentPlainText(doc: NoteDocument): string {
  const parts: string[] = []
  collectPlainText(doc, parts)
  return parts.join('')
}

export function isNoteDocumentEmpty(doc: NoteDocument): boolean {
  if (!Array.isArray(doc) || doc.length === 0) return true
  return noteDocumentPlainText(doc).trim() === ''
}

/** Compare documents ignoring block ids (BlockNote assigns ids on mount). */
export function noteDocumentsEqual(a: NoteDocument, b: NoteDocument): boolean {
  return stableSerialize(a) === stableSerialize(b)
}

function stableSerialize(doc: NoteDocument): string {
  return JSON.stringify(stripIds(doc))
}

function stripIds(blocks: NoteDocument): unknown[] {
  return blocks.map((block) => ({
    type: block.type,
    props: block.props ?? {},
    content: block.content ?? null,
    children: Array.isArray(block.children) ? stripIds(block.children) : [],
  }))
}

export function isAllowedNoteBlockType(type: unknown): type is AllowedNoteBlockType {
  return typeof type === 'string' && ALLOWED_TYPE_SET.has(type)
}

function isDangerousHref(href: unknown): boolean {
  if (typeof href !== 'string') return true
  const trimmed = href.trim().toLowerCase()
  return (
    trimmed.startsWith('javascript:') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('vbscript:')
  )
}

function scanInlineForDanger(value: unknown, path: string): string | null {
  if (value == null || typeof value === 'string') return null
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const err = scanInlineForDanger(value[i], `${path}[${i}]`)
      if (err) return err
    }
    return null
  }
  if (typeof value !== 'object') return `${path} has invalid inline content.`
  const record = value as Record<string, unknown>
  if (record.type === 'link') {
    if (isDangerousHref(record.href)) {
      return `${path}.href uses a disallowed URL scheme.`
    }
    return scanInlineForDanger(record.content, `${path}.content`)
  }
  if (record.content !== undefined) {
    const err = scanInlineForDanger(record.content, `${path}.content`)
    if (err) return err
  }
  return null
}

export type ValidateNoteDocumentOptions = {
  maxBlocks?: number
  maxDepth?: number
  maxJsonChars?: number
}

/**
 * Structural validation for persisted BlockNote JSON.
 * Rejects media blocks and dangerous link schemes.
 */
export function validateNoteDocument(
  raw: unknown,
  path: string,
  options: ValidateNoteDocumentOptions = {},
): NoteDocument | string {
  const maxBlocks = options.maxBlocks ?? 500
  const maxDepth = options.maxDepth ?? 24
  const maxJsonChars = options.maxJsonChars ?? 100_000

  if (!Array.isArray(raw)) return `${path} must be an array of blocks.`
  if (raw.length === 0) return `${path} must contain at least one block.`

  let jsonChars: number
  try {
    jsonChars = JSON.stringify(raw).length
  } catch {
    return `${path} is not JSON-serializable.`
  }
  if (jsonChars > maxJsonChars) {
    return `${path} exceeds ${maxJsonChars} characters when serialized.`
  }

  let blockCount = 0

  const walk = (blocks: unknown[], blockPath: string, depth: number): NoteDocument | string => {
    if (depth > maxDepth) return `${blockPath} exceeds max nesting depth (${maxDepth}).`
    const out: NoteBlock[] = []
    for (let i = 0; i < blocks.length; i++) {
      blockCount += 1
      if (blockCount > maxBlocks) {
        return `${path} exceeds max block count (${maxBlocks}).`
      }
      const item = blocks[i]
      const itemPath = `${blockPath}[${i}]`
      if (typeof item !== 'object' || item === null || Array.isArray(item)) {
        return `${itemPath} must be a block object.`
      }
      const record = item as Record<string, unknown>
      if (!isAllowedNoteBlockType(record.type)) {
        return `${itemPath}.type "${String(record.type)}" is not allowed.`
      }
      if (record.id !== undefined && typeof record.id !== 'string') {
        return `${itemPath}.id must be a string when present.`
      }
      if (record.props !== undefined) {
        if (typeof record.props !== 'object' || record.props === null || Array.isArray(record.props)) {
          return `${itemPath}.props must be an object when present.`
        }
      }
      if (record.content !== undefined) {
        const inlineErr = scanInlineForDanger(record.content, `${itemPath}.content`)
        if (inlineErr) return inlineErr
      }
      let children: NoteBlock[] | undefined
      if (record.children !== undefined) {
        if (!Array.isArray(record.children)) {
          return `${itemPath}.children must be an array when present.`
        }
        const childResult = walk(record.children, `${itemPath}.children`, depth + 1)
        if (typeof childResult === 'string') return childResult
        children = childResult
      }
      const block: NoteBlock = {
        type: record.type,
      }
      if (typeof record.id === 'string') block.id = record.id
      if (record.props && typeof record.props === 'object') {
        block.props = record.props as Record<string, unknown>
      }
      if (record.content !== undefined) block.content = record.content
      if (children) block.children = children
      out.push(block)
    }
    return out
  }

  return walk(raw, path, 0)
}
